import { z } from 'zod';

/** Every kind of notification, with the copy used in the preferences screen. */
export const NOTIFICATION_TYPES = [
  { type: 'assigned', label: 'Assigned to me', description: 'Someone assigns a task to you' },
  { type: 'mentioned', label: 'Mentions', description: 'Someone mentions you in a comment or message' },
  { type: 'comment', label: 'Comments', description: 'New comments on tasks you watch' },
  { type: 'comment_assigned', label: 'Assigned comments', description: 'Someone assigns a comment to you' },
  { type: 'status_changed', label: 'Status changes', description: 'The status changes on a task you watch' },
  { type: 'due_soon', label: 'Due soon', description: 'A task assigned to you is due within a day' },
  { type: 'overdue', label: 'Overdue', description: 'A task assigned to you is past its due date' },
  { type: 'reminder', label: 'Reminders', description: 'Reminders you set for yourself' },
  { type: 'dm', label: 'Direct messages', description: 'Someone sends you a direct message' },
  { type: 'form_submission', label: 'Form submissions', description: 'A form you created receives a submission' },
  { type: 'meeting', label: 'Meetings', description: 'Someone books an office room with you, or changes a meeting you are in' },
] as const;

export type NotificationType = (typeof NOTIFICATION_TYPES)[number]['type'];
export const NOTIFICATION_TYPE_IDS = NOTIFICATION_TYPES.map((t) => t.type) as [NotificationType, ...NotificationType[]];

export const NOTIFICATION_CHANNELS = ['inbox', 'email'] as const;
export type NotificationChannel = (typeof NOTIFICATION_CHANNELS)[number];

export interface NotificationDTO {
  id: string;
  type: NotificationType;
  resourceType: string;
  resourceId: string;
  actorId: string | null;
  /**
   * `title`: what it is about (task name, channel name). `text`: a short preview.
   * Plus link hints such as `commentId`, `channelId`, `messageId`.
   */
  data: { title?: string; text?: string; [key: string]: unknown };
  readAt: number | null;
  clearedAt: number | null;
  createdAt: number;
}

export interface NotificationPrefDTO {
  channel: NotificationChannel;
  type: NotificationType;
  enabled: boolean;
}

export interface ReminderDTO {
  id: string;
  taskId: string | null;
  /** Name of the linked task, when there is one and it still exists. */
  taskName: string | null;
  title: string;
  remindAt: number;
  done: boolean;
  delivered: boolean;
}

/** Sentence fragment after the actor's name, e.g. "Maya assigned you a task". */
export function notificationVerb(type: NotificationType): string {
  switch (type) {
    case 'assigned':
      return 'assigned you';
    case 'mentioned':
      return 'mentioned you in';
    case 'comment':
      return 'commented on';
    case 'comment_assigned':
      return 'assigned you a comment on';
    case 'status_changed':
      return 'changed the status of';
    case 'due_soon':
      return 'is due soon';
    case 'overdue':
      return 'is overdue';
    case 'reminder':
      return 'Reminder';
    case 'dm':
      return 'sent you a message';
    case 'form_submission':
      return 'was submitted through a form';
    case 'meeting':
      return 'booked a meeting with you';
  }
}

// ---------- Schemas ----------

const id = z.string().min(1).max(64);

export const notificationQuerySchema = z.object({
  filter: z.enum(['unread', 'all', 'cleared']).default('all'),
  limit: z.coerce.number().int().min(1).max(500).optional(),
});

/** Target specific notifications, everything about one resource, or all of them. */
export const notificationTargetSchema = z
  .object({
    ids: z.array(id).max(500).optional(),
    resourceType: z.string().max(32).optional(),
    resourceId: id.optional(),
    all: z.boolean().optional(),
  })
  .refine((v) => v.all || v.ids?.length || (v.resourceType && v.resourceId), 'Nothing selected');

export const setNotificationPrefsSchema = z.object({
  prefs: z
    .array(
      z.object({
        channel: z.enum(NOTIFICATION_CHANNELS),
        type: z.enum(NOTIFICATION_TYPE_IDS),
        enabled: z.boolean(),
      }),
    )
    .max(50),
});

export const createReminderSchema = z.object({
  title: z.string().trim().min(1).max(300),
  remindAt: z.number().int(),
  taskId: id.nullable().optional(),
});
export const updateReminderSchema = z.object({
  title: z.string().trim().min(1).max(300).optional(),
  remindAt: z.number().int().optional(),
  done: z.boolean().optional(),
});
